import { useState, useEffect } from 'react'
import { ArrowLeft, CheckCircle, RefreshCw, Wallet, BarChart2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
    getBalance,
    getCachedBalance,
    getFuturesBalance,
    recordInternalTransfer,
    getSpotHoldings,
    getLastKnownPrices,
    getSpotCostBasis,
} from '../../services/walletService'
import { useToast } from '../../context/ToastContext'
import Layout from '../../components/Layout/Layout'

import '../../styles/dashboard.css'

type AccountType = 'Spot' | 'Futures'

function TransferCrypto() {
    const navigate = useNavigate()
    const { toast } = useToast()

    const [fromAccount, setFromAccount] = useState<AccountType>('Spot')
    const [toAccount, setToAccount] = useState<AccountType>('Futures')
    const [spotBalance, setSpotBalance] = useState(() => getCachedBalance())
    const [spotHoldingsUsd, setSpotHoldingsUsd] = useState(0)
    const [futuresBalance, setFuturesBalanceState] = useState(() => getFuturesBalance())
    const [amount, setAmount] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [success, setSuccess] = useState(false)
    const [lastTransferred, setLastTransferred] = useState(0)
    const [swapping, setSwapping] = useState(false)
    const [balanceLoading, setBalanceLoading] = useState(true)

    useEffect(() => {
        const load = async () => {
            try {
                const bal = await getBalance()
                // Always trust the backend value directly — it is the source of truth,
                // including when the real balance is legitimately 0.
                setSpotBalance(bal.balance || 0)
            } catch { /* use cached */ } finally {
                setBalanceLoading(false)
            }
            setFuturesBalanceState(getFuturesBalance())
            const holdings = getSpotHoldings()
            const lastPrices = getLastKnownPrices()
            const costBasis = getSpotCostBasis()
            const holdingsUsd = Object.entries(holdings).reduce((sum, [sym, held]) => {
                if (!held || held <= 0) return sum
                const p = lastPrices[sym + '/USDT'] || lastPrices[sym + 'USDT'] || 0
                if (p > 0) return sum + held * p
                return sum + (costBasis[sym] || 0)
            }, 0)
            setSpotHoldingsUsd(holdingsUsd)
        }
        load()
    }, [])

    const spotTotal = spotBalance + spotHoldingsUsd
    const fromBalance = fromAccount === 'Spot' ? spotTotal : futuresBalance
    const toBalance   = toAccount   === 'Spot' ? spotTotal : futuresBalance

    const handleSwap = () => {
        setSwapping(true)
        setTimeout(() => {
            setFromAccount(toAccount)
            setToAccount(fromAccount)
            setAmount('')
            setSwapping(false)
        }, 180)
    }

    const handleMax = () => {
        if (fromBalance > 0) setAmount((Math.floor(fromBalance * 100) / 100).toFixed(2))
    }

    const setPercent = (pct: number) => {
        const val = (Math.floor(fromBalance * pct) / 100).toFixed(2)
        setAmount(val)
    }

    const handleTransfer = async () => {
        const amt = parseFloat(amount)
        if (!amt || isNaN(amt) || amt <= 0) { toast('Enter a valid amount', 'warning'); return }
        if (amt > parseFloat(fromBalance.toFixed(8))) { toast(`Insufficient ${fromAccount} balance`, 'error'); return }
        setIsSubmitting(true)
        try {
            const result = await recordInternalTransfer(amt, fromAccount, toAccount)
            setSpotBalance(result.balance)
            setFuturesBalanceState(result.futuresBalance)
            setLastTransferred(amt)
            setAmount('')
            setSuccess(true)
        } catch (e: any) {
            toast(e.message || 'Transfer failed', 'error')
        } finally {
            setIsSubmitting(false)
        }
    }

    const amt = parseFloat(amount)
    const isValid = !!(amount && amt > 0 && amt <= parseFloat(fromBalance.toFixed(8)))
    const pctFilled = isValid ? Math.min((amt / fromBalance) * 100, 100) : 0

    // ─── Success Screen ────────────────────────────────────────────────────────
    if (success) {
        return (
            <Layout activePage="assets" hideMobileNav={true} hideFooterMobile={true}>
                <div style={{ minHeight: '100vh', background: 'var(--ui-bg-primary)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', gap: 28 }}>
                    <div style={{ position: 'relative', width: 88, height: 88 }}>
                        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(28,212,167,0.08)', animation: 'pulse 2s infinite' }} />
                        <div style={{ position: 'absolute', inset: 8, borderRadius: '50%', background: 'rgba(28,212,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CheckCircle size={38} color="#1CD4A7" strokeWidth={1.8} />
                        </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 13, color: 'var(--ui-text-muted)', marginBottom: 6, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Transfer Complete</div>
                        <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--ui-text-primary)', letterSpacing: '-1px' }}>{lastTransferred.toFixed(2)} <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--ui-text-muted)' }}>USDT</span></div>
                        <div style={{ fontSize: 14, color: 'var(--ui-text-muted)', marginTop: 8 }}>
                            {fromAccount === 'Spot' ? 'Futures' : 'Spot'} → {fromAccount === 'Spot' ? 'Spot' : 'Futures'}
                        </div>
                    </div>

                    {/* Balance summary */}
                    <div style={{ width: '100%', maxWidth: 380, background: 'var(--ui-bg-card)', borderRadius: 18, border: '1px solid var(--ui-border)', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #1C1C2C' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1CD4A7' }} />
                                <span style={{ color: 'var(--ui-text-secondary)', fontSize: 13 }}>Spot Account</span>
                            </div>
                            <span style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 14 }}>{balanceLoading ? <span className="balance-skeleton balance-skeleton-sm" /> : `${spotTotal.toFixed(2)} USDT`}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#F59E0B' }} />
                                <span style={{ color: 'var(--ui-text-secondary)', fontSize: 13 }}>Futures Account</span>
                            </div>
                            <span style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 14 }}>{balanceLoading ? <span className="balance-skeleton balance-skeleton-sm" /> : `${futuresBalance.toFixed(2)} USDT`}</span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 380 }}>
                        <button onClick={() => setSuccess(false)}
                            style={{ background: '#1CD4A7', border: 'none', borderRadius: 14, padding: '16px', color: '#000', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Transfer Again
                        </button>
                        <button onClick={() => navigate(-1)}
                            style={{ background: 'transparent', border: '1px solid var(--ui-border)', borderRadius: 14, padding: '16px', color: 'var(--ui-text-muted)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Done
                        </button>
                    </div>
                </div>
            </Layout>
        )
    }

    // ─── Main Transfer Screen ──────────────────────────────────────────────────
    return (
        <Layout activePage="assets" hideMobileNav={true} hideFooterMobile={true}>
            <div style={{ minHeight: '100vh', background: 'var(--ui-bg-primary)', display: 'flex', flexDirection: 'column' }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px 16px', borderBottom: '1px solid #1C1C2C' }}>
                    <button onClick={() => navigate(-1)}
                        style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--ui-text-primary)', flexShrink: 0 }}>
                        <ArrowLeft size={18} />
                    </button>
                    <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ui-text-primary)', margin: 0 }}>Transfer</h1>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 40px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 480, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>


                    {/* From / Swap / To block */}
                    <div>
                        <div style={{ fontSize: 12, color: 'var(--ui-text-muted)', marginBottom: 8, fontWeight: 500 }}>Transfer</div>
                        <div style={{ background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 16, overflow: 'visible', position: 'relative' }}>

                            {/* From */}
                            <div style={{ padding: '16px 16px 14px' }}>
                                <div style={{ fontSize: 11, color: 'var(--ui-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.7px', fontWeight: 600, marginBottom: 6 }}>From</div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <div style={{ width: 36, height: 36, borderRadius: 10, background: fromAccount === 'Spot' ? 'rgba(28,212,167,0.12)' : 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>
                                            {fromAccount === 'Spot' ? <Wallet size={18} color="#1CD4A7" /> : <BarChart2 size={18} color="#F59E0B" />}
                                        </div>
                                        <div>
                                            <div style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 15 }}>{fromAccount} Account</div>
                                            <div style={{ color: 'var(--ui-text-muted)', fontSize: 12, marginTop: 2 }}>
                                                Avail: <span style={{ color: 'var(--ui-text-secondary)', fontWeight: 600 }}>{balanceLoading ? <span className="balance-skeleton balance-skeleton-sm" /> : `${fromBalance.toFixed(2)} USDT`}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Swap button - centered between */}
                            <div style={{ position: 'relative', height: 0, display: 'flex', justifyContent: 'center', zIndex: 10 }}>
                                <button
                                    onClick={handleSwap}
                                    style={{
                                        position: 'absolute', top: -18,
                                        width: 36, height: 36, borderRadius: '50%',
                                        background: 'var(--ui-bg-primary)', border: '2px solid var(--ui-border)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', color: '#1CD4A7',
                                        transform: swapping ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s ease',
                                    }}
                                >
                                    <RefreshCw size={15} />
                                </button>
                            </div>

                            {/* Divider */}
                            <div style={{ height: 1, background: 'var(--ui-bg-elevated)', margin: '0' }} />

                            {/* To */}
                            <div style={{ padding: '14px 16px 16px' }}>
                                <div style={{ fontSize: 11, color: 'var(--ui-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.7px', fontWeight: 600, marginBottom: 6 }}>To</div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <div style={{ width: 36, height: 36, borderRadius: 10, background: toAccount === 'Futures' ? 'rgba(245,158,11,0.12)' : 'rgba(28,212,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>
                                            {toAccount === 'Futures' ? <BarChart2 size={18} color="#F59E0B" /> : <Wallet size={18} color="#1CD4A7" />}
                                        </div>
                                        <div>
                                            <div style={{ color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 15 }}>{toAccount} Account</div>
                                            <div style={{ color: 'var(--ui-text-muted)', fontSize: 12, marginTop: 2 }}>
                                                Avail: <span style={{ color: 'var(--ui-text-secondary)', fontWeight: 600 }}>{balanceLoading ? <span className="balance-skeleton balance-skeleton-sm" /> : `${toBalance.toFixed(2)} USDT`}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Amount input */}
                    <div>
                        <div style={{ fontSize: 12, color: 'var(--ui-text-muted)', marginBottom: 8, fontWeight: 500 }}>Amount</div>
                        <div style={{ background: 'var(--ui-bg-card)', border: `1px solid ${amount && !isValid ? '#EF4444' : '#1C1C2C'}`, borderRadius: 14, padding: '14px 16px', transition: 'border-color 0.2s' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    value={amount}
                                    onChange={e => setAmount(e.target.value)}
                                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--ui-text-primary)', fontSize: 26, fontWeight: 700, fontFamily: 'inherit', minWidth: 0 }}
                                />
                                <span style={{ color: 'var(--ui-text-muted)', fontSize: 14, fontWeight: 600, flexShrink: 0 }}>USDT</span>
                                <button
                                    onClick={handleMax}
                                    style={{ background: 'rgba(28,212,167,0.1)', border: '1px solid rgba(28,212,167,0.2)', borderRadius: 6, color: '#1CD4A7', fontWeight: 700, fontSize: 11, padding: '4px 10px', cursor: 'pointer', flexShrink: 0, letterSpacing: '0.4px', fontFamily: 'inherit' }}>
                                    MAX
                                </button>
                            </div>

                            {/* Progress bar */}
                            <div style={{ marginTop: 12, height: 3, background: 'var(--ui-bg-elevated)', borderRadius: 99, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${pctFilled}%`, background: pctFilled > 90 ? '#EF4444' : '#1CD4A7', borderRadius: 99, transition: 'width 0.2s, background 0.2s' }} />
                            </div>

                            {/* Percent quick select */}
                            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                {[25, 50, 75, 100].map(p => (
                                    <button key={p} onClick={() => setPercent(p)}
                                        style={{ flex: 1, background: 'var(--ui-bg-primary)', border: '1px solid var(--ui-border)', borderRadius: 6, color: 'var(--ui-text-muted)', fontSize: 12, fontWeight: 600, padding: '5px 0', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                                        {p}%
                                    </button>
                                ))}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 12, color: 'var(--ui-text-secondary)' }}>
                                <span>Available: <span style={{ color: 'var(--ui-text-secondary)' }}>{balanceLoading ? <span className="balance-skeleton balance-skeleton-sm" /> : `${fromBalance.toFixed(2)} USDT`}</span></span>
                                {amount && amt > 0 && <span style={{ color: amt > fromBalance ? '#EF4444' : '#52526a' }}>≈ ${amt.toLocaleString(undefined, { minimumFractionDigits: 2 })} USD</span>}
                            </div>
                        </div>
                        {amount && !isValid && amt > fromBalance && (
                            <div style={{ color: '#EF4444', fontSize: 12, marginTop: 6, paddingLeft: 4 }}>Insufficient balance</div>
                        )}
                    </div>

                    {/* Transfer button */}
                    <button
                        onClick={handleTransfer}
                        disabled={!isValid || isSubmitting}
                        style={{
                            background: isValid ? '#1CD4A7' : 'var(--ui-bg-elevated)',
                            border: isValid ? 'none' : '1px solid var(--ui-border)', borderRadius: 14, padding: '16px',
                            color: isValid ? '#000' : 'var(--ui-text-muted)',
                            fontSize: 15, fontWeight: 700, width: '100%',
                            cursor: isValid ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s', fontFamily: 'inherit',
                            opacity: isSubmitting ? 0.7 : 1,
                        }}
                    >
                        {isSubmitting ? 'Processing...' : `Transfer to ${toAccount}`}
                    </button>

                    {/* Info rows */}
                    <div style={{ background: 'var(--ui-bg-card)', borderRadius: 14, border: '1px solid var(--ui-border)', overflow: 'hidden' }}>
                        {[
                            ['Transaction Fee', 'Free'],
                            ['Processing Time', 'Instant'],
                            ['Minimum Amount', '0.01 USDT'],
                        ].map(([label, value], i, arr) => (
                            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px', borderBottom: i < arr.length - 1 ? '1px solid #1C1C2C' : 'none' }}>
                                <span style={{ color: 'var(--ui-text-muted)', fontSize: 13 }}>{label}</span>
                                <span style={{ color: value === 'Free' ? '#1CD4A7' : value === 'Instant' ? '#a1a1aa' : '#a1a1aa', fontSize: 13, fontWeight: 600 }}>{value}</span>
                            </div>
                        ))}
                    </div>

                </div>
            </div>
        </Layout>
    )
}

export default TransferCrypto
